import { FC, useCallback, useEffect, useRef, useState } from 'react';
import stylex from '@stylexjs/stylex';
import InputClearable from '@shared/ui/input-clearable';
import {
  type GuestInfo,
  type FieldInfo,
  FIELD_ID_PHONE,
  FIELD_ID_EMAIL,
  validationFieldsGuest,
} from '@entities/leadCollector';
import { phoneNumberValidator } from '@shared/libs/phone-validator';
import { ERROR_PHONE_MESSAGE } from '@shared/constants/errorMessages';
import { styles } from '../MainGuestTab/styles';

interface MainGuestFromFormBodyProps {
  mainGuest: GuestInfo;
  registerValidate?: (validate: () => Promise<boolean>) => void;
  onChange?: (guest: GuestInfo) => void;
}

const initGuest = (guest: GuestInfo): GuestInfo => ({
  FIO: guest.FIO.map((field) => ({ ...field, value: '', error: '' })),
  additionalFields: (guest.additionalFields ?? []).map((field) => ({ ...field, value: '', error: '' })),
});

const updateFieldInGuest = (prev: GuestInfo, instanceId: string, value: string, error = ''): GuestInfo => {
  const patch = (field: FieldInfo) => (field.instanceId === instanceId ? { ...field, value, error } : field);
  return {
    FIO: prev.FIO.map(patch),
    additionalFields: (prev.additionalFields ?? []).map(patch),
  };
};

export const MainGuestFromFormBody: FC<MainGuestFromFormBodyProps> = ({ mainGuest, registerValidate, onChange }) => {
  const [guest, setGuest] = useState<GuestInfo>(() => initGuest(mainGuest));
  const guestRef = useRef(guest);
  guestRef.current = guest;

  useEffect(() => {
    setGuest(initGuest(mainGuest));
  }, [mainGuest]);

  const updateField = useCallback((instanceId: string, value: string, error = '') => {
    setGuest((prev) => updateFieldInGuest(prev, instanceId, value, error));
  }, []);

  const runValidation = useCallback(async (): Promise<boolean> => {
    const copy = structuredClone(guestRef.current);
    await validationFieldsGuest(copy);
    setGuest(copy);
    const hasError =
      copy.FIO.some((field) => field.error) || (copy.additionalFields ?? []).some((field) => field.error);
    return !hasError;
  }, []);

  useEffect(() => {
    registerValidate?.(runValidation);
    return () => registerValidate?.(undefined as unknown as () => Promise<boolean>);
  }, [registerValidate, runValidation]);

  useEffect(() => {
    onChange?.(guestRef.current);
  }, [guest, onChange]);

  const handlePhoneChange = useCallback(
    (instanceId: string, value: string) => {
      const masked = phoneNumberValidator(value);
      const errorMessage = masked.length && masked.replace(/\D/g, '').length < 10 ? ERROR_PHONE_MESSAGE : '';
      updateField(instanceId, masked, errorMessage);
    },
    [updateField]
  );

  const renderField = (field: FieldInfo) => {
    const isPhone = field.dbField === FIELD_ID_PHONE;
    const isEmail = field.dbField === FIELD_ID_EMAIL;
    const label = field.label || field.placeholder || field.dbField;
    const status: 'error' | undefined = field.error ? 'error' : undefined;
    const changeFunction = (inputValue: string) => {
      if (isPhone) handlePhoneChange(field.instanceId, inputValue);
      else updateField(field.instanceId, inputValue);
    };

    const common = {
      status,
      size: 'small' as const,
      settings: { width: '267px' },
      value: field.value,
      changeFunction,
      clearHandler: () => updateField(field.instanceId, ''),
    };

    const placeholder = (() => {
      if (isPhone) return '+ 7 (000) 000-00-00';
      if (isEmail) return 'Укажите e-mail';
      return field.placeholder || '';
    })();

    return (
      <div key={field.instanceId}>
        <div {...stylex.props(styles.fieldWithLabel)}>
          <span {...stylex.props(styles.fieldLabel)}>{label}</span>
          <InputClearable {...common} placeholder={placeholder} inputMessage={field.error} />
        </div>
      </div>
    );
  };

  const fio = guest.FIO ?? [];
  const additionalFields = guest.additionalFields ?? [];
  const allFieldsInOrder = [...fio, ...additionalFields];

  return (
    <div {...stylex.props(styles.sections)}>
      <section {...stylex.props(styles.section)}>
        <h3 {...stylex.props(styles.sectionTitle)}>Основной гость</h3>
        <div {...stylex.props(styles.mainGuestFields)}>
          {!!allFieldsInOrder.length && (
            <div {...stylex.props(styles.fieldRow)}>{allFieldsInOrder.map((field) => renderField(field))}</div>
          )}
        </div>
      </section>
    </div>
  );
};
